/**
 * GraphQL Task Mutation Hooks
 *
 * Hooks for task operations: create, update status, update progress, delete.
 * Feature: 078-user-tasks
 */

import { useCallback } from 'react';
import { useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// ============================================================================
// GraphQL Mutations
// ============================================================================

const CREATE_TASK_MUTATION = gql`
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(input: $input)
  }
`;

const UPDATE_TASK_STATUS_MUTATION = gql`
  mutation UpdateTaskStatus($input: UpdateTaskStatusInput!) {
    updateTaskStatus(input: $input)
  }
`;

const UPDATE_TASK_PROGRESS_MUTATION = gql`
  mutation UpdateTaskProgress($input: UpdateTaskProgressInput!) {
    updateTaskProgress(input: $input)
  }
`;

const DELETE_TASK_MUTATION = gql`
  mutation DeleteTask($input: DeleteTaskInput!) {
    deleteTask(input: $input)
  }
`;

const COMPLETE_ALL_TASKS_MUTATION = gql`
  mutation CompleteAllTasks {
    completeAllTasks
  }
`;

const DISMISS_ALL_TASKS_MUTATION = gql`
  mutation DismissAllTasks {
    dismissAllTasks
  }
`;

// Feature 651: Complete VIEW_PHOTO task by target criteria
const COMPLETE_VIEW_PHOTO_TASK_MUTATION = gql`
  mutation CompleteViewPhotoTask($input: CompleteViewPhotoTaskInput!) {
    completeViewPhotoTask(input: $input)
  }
`;

// ============================================================================
// Types
// ============================================================================

export type TaskActionType =
  | 'VIEW_PHOTO'
  | 'COMMENT_ON_PHOTO'
  | 'RATE_PHOTO'
  | 'REPLY_TO_COMMENT'
  | 'COMMENT_ON_N_PHOTOS'  // Feature 651: Aggregate N Comments task
  | 'MANUAL';

export type TaskStatus = 'pending' | 'completed' | 'dismissed';

export type TaskTargetType = 'photo' | 'comment' | 'photo_in_critique' | 'critique_session';

export interface Task {
  id: string;
  globalId: string;
  actionType: TaskActionType;
  status: TaskStatus;
  targetType: TaskTargetType;
  targetId: string;
  targetUrl: string;
  targetContext: Record<string, unknown> | null;
  description: string | null;
  progressCurrent: number;
  progressRequired: number | null;
  sourceType: string;
  sourceId: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  hasProgress: boolean;
  progressPercent: number | null;
}

export interface CreateTaskInput {
  actionType: TaskActionType;
  targetType: TaskTargetType;
  targetId: string;
  targetContext?: Record<string, unknown>;
  description?: string;
  progressRequired?: number;
}

export interface UpdateTaskStatusInput {
  taskId: string;
  status: TaskStatus;
}

export interface UpdateTaskProgressInput {
  taskId: string;
  increment?: number;
}

export interface DeleteTaskInput {
  taskId: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for creating a new task
 */
export function useCreateTaskGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { createTask: Task },
    { input: CreateTaskInput }
  >(CREATE_TASK_MUTATION, {
    refetchQueries: ['Tasks', 'TaskSummary'],
    onCompleted: (data) => {
      logger.info('Tasks', 'Task created via GraphQL', {
        taskId: data.createTask.id,
        actionType: data.createTask.actionType,
      });
    },
    onError: (err) => {
      logger.error('Tasks', 'Failed to create task', err);
    },
  });

  const createTask = async (input: CreateTaskInput) => {
    const result = await mutate({
      variables: { input },
    });
    return result.data?.createTask;
  };

  return { createTask, loading, error };
}

/**
 * Hook for updating task status
 */
export function useUpdateTaskStatusGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { updateTaskStatus: Task },
    { input: UpdateTaskStatusInput }
  >(UPDATE_TASK_STATUS_MUTATION, {
    refetchQueries: ['Tasks', 'TaskSummary'],
    onCompleted: (data) => {
      logger.info('Tasks', 'Task status updated via GraphQL', {
        taskId: data.updateTaskStatus.id,
        status: data.updateTaskStatus.status,
      });
    },
    onError: (err) => {
      logger.error('Tasks', 'Failed to update task status', err);
    },
  });

  const updateStatus = async (taskId: string, status: TaskStatus) => {
    const result = await mutate({
      variables: { input: { taskId, status } },
    });
    return result.data?.updateTaskStatus;
  };

  return { updateStatus, loading, error };
}

/**
 * Hook for updating task progress
 */
export function useUpdateTaskProgressGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { updateTaskProgress: Task },
    { input: UpdateTaskProgressInput }
  >(UPDATE_TASK_PROGRESS_MUTATION, {
    refetchQueries: ['Tasks', 'TaskSummary'],
    onCompleted: (data) => {
      logger.info('Tasks', 'Task progress updated via GraphQL', {
        taskId: data.updateTaskProgress.id,
        progressCurrent: data.updateTaskProgress.progressCurrent,
        status: data.updateTaskProgress.status,
      });
    },
    onError: (err) => {
      logger.error('Tasks', 'Failed to update task progress', err);
    },
  });

  const updateProgress = async (taskId: string, increment: number = 1) => {
    const result = await mutate({
      variables: { input: { taskId, increment } },
    });
    return result.data?.updateTaskProgress;
  };

  return { updateProgress, loading, error };
}

/**
 * Hook for deleting a task
 */
export function useDeleteTaskGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { deleteTask: boolean },
    { input: DeleteTaskInput }
  >(DELETE_TASK_MUTATION, {
    refetchQueries: ['Tasks', 'TaskSummary'],
    onCompleted: () => {
      logger.info('Tasks', 'Task deleted via GraphQL');
    },
    onError: (err) => {
      logger.error('Tasks', 'Failed to delete task', err);
    },
  });

  const deleteTask = async (taskId: string) => {
    const result = await mutate({
      variables: { input: { taskId } },
    });
    return result.data?.deleteTask ?? false;
  };

  return { deleteTask, loading, error };
}

/**
 * Hook for completing all tasks
 */
export function useCompleteAllTasksGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { completeAllTasks: number }
  >(COMPLETE_ALL_TASKS_MUTATION, {
    refetchQueries: ['Tasks', 'TaskSummary'],
    onCompleted: (data) => {
      logger.info('Tasks', 'All tasks completed via GraphQL', {
        count: data.completeAllTasks,
      });
    },
    onError: (err) => {
      logger.error('Tasks', 'Failed to complete all tasks', err);
    },
  });

  const completeAll = async () => {
    const result = await mutate();
    return result.data?.completeAllTasks ?? 0;
  };

  return { completeAll, loading, error };
}

/**
 * Hook for dismissing all tasks
 */
export function useDismissAllTasksGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { dismissAllTasks: number }
  >(DISMISS_ALL_TASKS_MUTATION, {
    refetchQueries: ['Tasks', 'TaskSummary'],
    onCompleted: (data) => {
      logger.info('Tasks', 'All tasks dismissed via GraphQL', {
        count: data.dismissAllTasks,
      });
    },
    onError: (err) => {
      logger.error('Tasks', 'Failed to dismiss all tasks', err);
    },
  });

  const dismissAll = async () => {
    const result = await mutate();
    return result.data?.dismissAllTasks ?? 0;
  };

  return { dismissAll, loading, error };
}

/**
 * Hook for completing VIEW_PHOTO task by target criteria
 * Feature 651: Auto-complete VIEW_PHOTO task when viewing a photo in critique
 */
export function useCompleteViewPhotoTaskGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { completeViewPhotoTask: Task | null },
    { input: { sessionId: string; photoId: string } }
  >(COMPLETE_VIEW_PHOTO_TASK_MUTATION, {
    refetchQueries: ['Tasks', 'TaskSummary'],
    onCompleted: (data) => {
      if (data.completeViewPhotoTask) {
        logger.info('Tasks', 'VIEW_PHOTO task auto-completed', {
          taskId: data.completeViewPhotoTask.id,
        });
      }
    },
    onError: (err) => {
      // Don't log errors as this is a fire-and-forget operation
      // Task may not exist or already be complete
      logger.debug('Tasks', 'Failed to auto-complete VIEW_PHOTO task', err);
    },
  });

  const completeViewPhotoTask = useCallback(async (sessionId: string, photoId: string) => {
    const result = await mutate({
      variables: { input: { sessionId, photoId } },
    });
    return result.data?.completeViewPhotoTask ?? null;
  }, [mutate]);

  return { completeViewPhotoTask, loading, error };
}
